import React, { useState, useRef } from 'react';
import { BASE_API } from './constants';
import styles from './ProfilePage.module.css';

interface NurseDetails {
  firstName: string;
  lastName: string;
  email: string;
  pictureUrl?: string;
  nickname?: string;
  gender?: string;
  city?: string;
  state?: string;
}

interface ProfilePageProps {
  nurseDetails: NurseDetails;
  nurseId?: string;
  accessToken?: string;
  onProfileUpdate?: (updated: NurseDetails) => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ nurseDetails, nurseId, accessToken, onProfileUpdate }) => {
  const [form, setForm] = useState({
    firstName: nurseDetails.firstName || '',
    lastName: nurseDetails.lastName || '',
    gender: nurseDetails.gender || '',
    city: nurseDetails.city || '',
    state: nurseDetails.state || '',
    pictureUrl: nurseDetails.pictureUrl || '',
    email: nurseDetails.email || '',
    nickname: nurseDetails.nickname || '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !nurseId || !accessToken) return;
    setUploading(true);
    setError(null);
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('profilePic', file);
    try {
      const res = await fetch(`${BASE_API}/nurses/${nurseId}/upload/picture`, {
        method: 'POST',
        headers: { 'x-access-token': accessToken },
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.pictureUrl) {
        setForm(f => ({ ...f, pictureUrl: data.pictureUrl }));
      } else {
        setError(data.error || 'Failed to upload picture');
      }
    } catch (err: any) {
      setError('Failed to upload picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!nurseId || !accessToken) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_API}/nurses/${nurseId}/auth0/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': accessToken,
        },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          gender: form.gender || '',
          city: form.city,
          state: form.state,
          pictureUrl: form.pictureUrl,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        if (onProfileUpdate) onProfileUpdate({
          firstName: form.firstName,
          lastName: form.lastName,
          gender: form.gender || '',
          city: form.city,
          state: form.state,
          pictureUrl: form.pictureUrl,
          email: nurseDetails.email,
          nickname: nurseDetails.nickname,
        });
      } else {
        setError(data.error || 'Failed to update profile');
      }
    } catch (err: any) {
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.profilePageContainer}>
      <div className={styles.profileCard}>
        <h1 className={styles.profileTitle}>Edit Profile</h1>
        {form.pictureUrl ? (
          <img src={form.pictureUrl} alt={form.firstName} className={styles.profileImage} />
        ) : (
          <div className={styles.profileAvatar}>
            {form.firstName?.[0] || '?'}
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handlePictureChange}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className={styles.profileUploadButton}
        >
          {uploading ? 'Uploading...' : 'Change Picture'}
        </button>
        <div style={{ width: '100%', marginBottom: 10 }}>
          <input name="firstName" value={form.firstName} onChange={handleInputChange} placeholder="First Name" className={styles.profileInput} />
          <input name="lastName" value={form.lastName} onChange={handleInputChange} placeholder="Last Name" className={styles.profileInput} />
          <input name="city" value={form.city} onChange={handleInputChange} placeholder="City" className={styles.profileInput} />
          <input name="state" value={form.state} onChange={handleInputChange} placeholder="State" className={styles.profileInput} />
          <select name="gender" value={form.gender} onChange={handleInputChange} className={styles.profileSelect}>
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        {error && <div className={styles.profileError}>{error}</div>}
        <button onClick={handleSave} disabled={saving} className={styles.profileSaveButton}>{saving ? 'Saving...' : 'Save Changes'}</button>
      </div>
    </div>
  );
};

export default ProfilePage; 